import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { passwordStrength } from 'check-password-strength';

import './PasswordField.pcss';

const passwordStrengthOptions = [
  {
    id: 0,
    value: "Too weak",
    minDiversity: 0,
    minLength: 0,
  },
  {
    id: 1,
    value: "Weak",
    minDiversity: 2,
    minLength: 6
  },
  {
    id: 2,
    value: "Medium",
    minDiversity: 3,
    minLength: 8,
  },
  {
    id: 3,
    value: "Strong",
    minDiversity: 4,
    minLength: 10,
  }
];

interface IPasswordFieldProps {
  password:string;
  onPasswordChange:(password:string, passwordStrength:IPasswordStrength|null)=>void;
  email?:string;
  disabled?:boolean;
  error?:boolean;
  firstName?:string;
  lastName?:string;
  inputName:string;
  placeholder?:string;
  invalid?:boolean;
  required?:boolean;
  id?:string;
  hideStrengthHint?:boolean
}

export interface IPasswordStrength {
  contains:string[];
  id:number;
  length:number;
  value:string;

}

@observer
export class PasswordField extends React.Component<IPasswordFieldProps> {
  @observable show:boolean = false;
  @observable passwordStrength:IPasswordStrength|null = null;

  handleChange = (e:any) => {
    // @ts-ignore
    this.passwordStrength = passwordStrength(e.target.value, passwordStrengthOptions);
    this.props.onPasswordChange(e.target.value, this.passwordStrength );
  }

  @computed
  get score(){
    if(this.passwordStrength) {
      return this.passwordStrength.id;
    } else {
      return 0;
    }
  }

  @computed 
  get passwordFails() {
    return this.score<2;
  }

  @computed
  get passwordScoreLabel() {
    return passwordScore[this.score].label;
  }

  /**
   * Don't bother showing password strength until the minimum 8 chars is reached
   */
  @computed get showStrength() {
    return this.props.password && this.props.password.length > 7 ? true : false;
  }

  @computed get howManyCharsLeft() {
    return this.props.password ? 8 - this.props.password.length : 8;
  }

  render() {
    const { password, disabled, error} = this.props;

    return (
      <div className="passwordField" style={{marginBottom:`${this.props.hideStrengthHint?'0px':'16px'}`}}>
        <button tabIndex={-1} aria-hidden={true} className="show" onClick={(e)=>{e.preventDefault(); this.show = !this.show; }}><i className={`fa fa-${this.show?'eye':'eye-slash'}`}></i></button>
        <input
          aria-required={this.props.required}
          aria-invalid={this.props.invalid}
          autoComplete={"password"}
          name={"password"}
          type={`${this.show?'text':'password'}`}
          value={password}
          onChange={this.handleChange}
          placeholder={this.props.placeholder?this.props.placeholder:"enter your password"}
          disabled={disabled} 
          className={`${error ? 'error':''}`}
          id={this.props.id}
          style={{marginBottom:`${this.props.hideStrengthHint?'16px':'0px'}`}}
        />
        {!this.props.hideStrengthHint &&
          <div className={"passwordStrength"}>
            {this.showStrength ?
              <span role={"alert"} aria-live="polite" style={{color:`${passwordScore[this.score].color}`}}>
                <p>Password strength: {passwordScore[this.score].label}</p>
              </span>
              :
              <span><p>{this.howManyCharsLeft} more character{1===this.howManyCharsLeft?'':'s'} required.</p></span>
            }
          </div>
        }
      </div>
    );
  }
}

const passwordScore = [
  {
    score:0,
    label:'Very Weak',
    color:'#8A2E1E',
    colorLight:'rgba(209,70,47,.2)'
  },
  {
    score:1,
    label:'Weak',
    color:'#8A2E1E',
    colorLight:'rgba(209,70,47,.2)'
  },
  {
    score:2,
    label:'Medium',
    color:'#293ccc',
    colorLight:'rgba(87,184,255,.2)'
  },
  {
    score:3,
    label:'Strong',
    color:'#18633B',
    colorLight:'rgba(47,191,113,.2)'
  }
];
